import { getGDPRConsent, storeGDPRConsent, isGDPRCountry } from "./serviceConsentManager";
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from "./cookieUtils";

class CMPService {
  constructor() {
    this.USP_CONSENT_KEY = "uspConsentState";
    this.GPP_CONSENT_KEY = "gppConsentState";
    this.isInitialized = false;
    this.consentListeners = [];

    this.isGDPR = isGDPRCountry();
    this.showBanner = false;

    this.consentData = {};
    this.uspConsent = "1YNN";
    this.gppConsent = "GPP0";

    this.eventListeners = {};

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.ensureIframe("__tcfapiLocator");
    this.ensureIframe("__uspapiLocator");
    this.ensureIframe("__gppLocator");

    this.loadConsent();

    this.initTCFAPI();
    this.initUSPAPI();
    this.initGPPAPI();

    this.isInitialized = true;
  }

  ensureIframe(name) {
    if (!window.frames[name]) {
      const frame = document.createElement("iframe");
      frame.style.display = "none";
      frame.name = name;
      document.body.appendChild(frame);
    }
  }

  loadConsent() {
    if (this.isInitialized) {
      return;
    }

    if (this.isGDPR) {

      this.consentData = getGDPRConsent();

      if (!this.consentData.tcString) {
        this.showBanner = true;
        //console.warn("[CMP] No GDPR consent found. Showing banner.");
      } else {
        //console.log("[CMP] GDPR Consent loaded:", this.consentData);
      }
    } else {

      this.consentData = {
        gdprApplies: 0,
        tcString: "",
        storedPurposeConsents: {},
        storedVendorConsents: {},
        storedLegitimateInterests: {},
        storedSpecialFeatureOptIns: {},
        storedFeatures: {},
        storedVendorLegitimateInterests: {},
      };
    }

    this.uspConsent = getLocalStorageItem(this.USP_CONSENT_KEY, "1YNN");

    this.gppConsent = getLocalStorageItem(this.GPP_CONSENT_KEY, "GPP0");

    this.notifyConsentListeners();
  }

  subscribeToConsentChanges(callback) {
    this.consentListeners.push(callback);
    return () => {
      this.consentListeners = this.consentListeners.filter(listener => listener !== callback);
    };
  }

  notifyConsentListeners() {
    this.consentListeners.forEach(callback => {
      callback({
        isGDPR: this.isGDPR,
        showBanner: this.showBanner,
        uspConsent: this.uspConsent,
        gppConsent: this.gppConsent,
        consentData: this.consentData,
      });
    });
  }

  addEventListener(callback) {
    const listenerId = Object.keys(this.eventListeners).length + 1;
    this.eventListeners[listenerId] = callback;

    return listenerId;
  }

  removeEventListener(listenerId) {
    if (this.eventListeners[listenerId]) {
      delete this.eventListeners[listenerId];
      return true;
    }
    return false;
  }

  initTCFAPI() {
    window.__tcfapi = (cmd, version, callback, parameter) => {

      const tcData = { ...this.consentData, eventStatus: "tcloaded", cmpStatus: "loaded" };

      switch (cmd) {
        case "ping":
          callback({ gdprApplies: tcData.gdprApplies, cmpLoaded: true, cmpStatus: "loaded" }, true);
          break;

        case "getTCData":
          callback(tcData, true);
          break;

        case "addEventListener": {
          const listenerId = this.addEventListener(callback);
          callback({ ...tcData, listenerId }, true);
          break;
        }

        case "removeEventListener": {
          const success = this.removeEventListener(parameter);
          callback({ success }, success);
          break;
        }

        case "setConsent":
          storeGDPRConsent(parameter);
          Object.assign(this.consentData, parameter);
          Object.values(this.eventListeners).forEach(listener => listener(tcData, true));

          this.notifyConsentListeners(); // Notify all listeners about consent change
          callback({ success: true }, true);
          break;

        default:
          callback(null, false);
      }
    };

    window.__cmp = window.__tcfapi;
  }

  initUSPAPI() {
    window.__uspapi = (cmd, version, callback) => {

      if (cmd === "getUSPData" && version === 1) {
        callback({ uspString: this.uspConsent, version: 1 });
      } else if (cmd === "registerDeletion") {
        console.warn("[CMP] User requested data deletion. Removing USP consent...");
        removeLocalStorageItem(this.USP_CONSENT_KEY);
        callback({ success: true }, true);
        this.notifyConsentListeners();
      } else {
        callback(null, false);
      }
    };

  }

  initGPPAPI() {
    window.__gppapi = (cmd, version, callback) => {

      if (cmd === "getGPPData" && version === 1) {
        callback({ gppString: this.gppConsent, version: 1 });
      } else if (cmd === "registerDeletion") {
        console.warn("[CMP] User requested data deletion. Removing GPP consent...");
        removeLocalStorageItem(this.GPP_CONSENT_KEY);
        callback({ success: true }, true);
        this.notifyConsentListeners();
      } else {
        callback(null, false);
      }
    };

  }
}

const cmpInstance = new CMPService();
export default cmpInstance;
